import React from "react";
import "./RestrictedScreen.scss";

// Static, non-interactive copy of the Quiz & Survey page (top of the page).
// Shown instead of the app when FULL_ACCESS_WEB is false.
// No scrolling, no clicks, no focus, EXCEPT the Logout button in the header.
// The chatbot iframe sits above everything.

// Set to false if the browser already shows its own page scrollbar.
const SHOW_FAKE_SCROLLBAR = true;

const svgProps = {
  viewBox: "0 0 48 48",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "1.6",
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const ICONS = {
  staff: (
    <svg {...svgProps}>
      <circle cx="19" cy="14" r="6.5" />
      <path d="M30 8.5a6.5 6.5 0 0 1 0 11" />
      <path d="M6 38v-3a10 10 0 0 1 10-10h6a10 10 0 0 1 10 10v3" />
      <path d="M36 27a9 9 0 0 1 6 8v3" />
    </svg>
  ),
  usage: (
    <svg {...svgProps}>
      <path d="M10 6v36M24 6v36M38 6v36" />
      <path d="M4 16h12M18 30h12M32 22h12" />
    </svg>
  ),
  training: (
    <svg {...svgProps}>
      <path d="M13 22v18M24 8v32M35 16v24" />
    </svg>
  ),
  bell: (
    <svg {...svgProps}>
      <path d="M24 5a2 2 0 0 1 2 2v1.2A12 12 0 0 1 36 20v8l4 6H8l4-6v-8a12 12 0 0 1 10-11.8V7a2 2 0 0 1 2-2z" />
      <path d="M21 39a3 3 0 0 0 6 0" />
    </svg>
  ),
  sales: (
    <svg {...svgProps}>
      <path d="M11 5h18l10 10v28H11z" />
      <path d="M28 5v11h11" />
      <path d="M25 23v14M18 30h14" />
    </svg>
  ),
  region: (
    <svg {...svgProps}>
      <circle cx="24" cy="24" r="19" />
      <ellipse cx="24" cy="24" rx="8" ry="19" />
      <path d="M5 24h38M24 5v38" />
    </svg>
  ),
  box: (
    <svg {...svgProps}>
      <rect x="9" y="8" width="30" height="12" rx="1" />
    </svg>
  ),
};

const SIDEBAR = [
  { icon: "staff", lines: ["Staff", "Performance"] },
  { icon: "usage", lines: ["System", "Usage", "Report"] },
  { icon: "training", lines: ["Training", "Report"] },
  { icon: "bell", lines: ["Upload", "Notifications"] },
  { icon: "sales", lines: ["Import Sales"] },
  { icon: "region", lines: ["Manage", "Region"] },
  { icon: "box", lines: [] },
];

const CARDS = [
  { title: "Untitled Survey", questions: 2 },
  { title: "Untitled Survey 23", questions: 1 },
  { title: "Untitled Survey", questions: 1 },
];

const Chevron = () => (
  <svg className="rs-chev" viewBox="0 0 12 8" fill="none">
    <path
      d="M1.5 1.5 6 6l4.5-4.5"
      stroke="#111"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const handleLogout = () => {
  localStorage.clear();
  // Full reload so every in-memory state (redux etc.) is reset too
  window.location.replace(`${process.env.PUBLIC_URL}/login`);
};

const RestrictedScreen = () => (
  <div
    className={`restricted-screen${
      SHOW_FAKE_SCROLLBAR ? " restricted-screen--sb" : ""
    }`}
  >
    {/* ---------- Header ---------- */}
    <div className="rs-header">
      <div className="rs-logo" aria-hidden="true">
        <div className="rs-logo__ar">عمانتل</div>
        <div className="rs-logo__en">
          Oman<span>tel</span>
        </div>
      </div>
      <div className="rs-header__title">Omantel SIP</div>
      <div className="rs-header__right">
        {/* Only clickable thing on this screen (besides the chatbot) */}
        <button type="button" className="rs-logout" onClick={handleLogout}>
          Logout
        </button>

        <svg
          viewBox="0 0 28 28"
          width="26"
          height="26"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="11" cy="9" r="4.5" stroke="#5b7a99" strokeWidth="1.2" />
          <path
            d="M3 22v-2a6 6 0 0 1 6-6h4"
            stroke="#5b7a99"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <circle cx="20" cy="19" r="3" stroke="#f58220" strokeWidth="1.3" />
          <circle
            cx="20"
            cy="19"
            r="5.2"
            stroke="#f58220"
            strokeWidth="1.3"
            strokeDasharray="2 2.2"
          />
        </svg>
        <svg
          className="rs-header__chev"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2 2l4 4 4-4"
            stroke="#5b7a99"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>

    {/* ---------- Sidebar ---------- */}
    <div className="rs-sidebar" aria-hidden="true">
      {SIDEBAR.map((item) => (
        <div className="rs-sidebar__item" key={item.icon}>
          <div className="rs-sidebar__icon">{ICONS[item.icon]}</div>
          {item.lines.map((line) => (
            <div className="rs-sidebar__label" key={line}>
              {line}
            </div>
          ))}
        </div>
      ))}
    </div>

    {/* ---------- Content ---------- */}
    <div className="rs-content" aria-hidden="true">
      <div className="rs-pagehead">
        <h1>Quiz &amp; Survey</h1>
        <div className="rs-breadcrumb">
          <svg viewBox="0 0 20 20" width="18" height="18" fill="none">
            <path
              d="M3 9.5 10 3l7 6.5V17H3z"
              stroke="#0a5ea8"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <path d="M8 17v-5h4v5" stroke="#0a5ea8" strokeWidth="1.5" />
          </svg>
          <span>/</span>
          <span>Quiz &amp; Survey</span>
        </div>
      </div>

      <div className="rs-inner">
        <div className="rs-search">
          <svg viewBox="0 0 20 20" width="18" height="18" fill="none">
            <circle cx="9" cy="9" r="6" stroke="#666" strokeWidth="1.4" />
            <path
              d="m13.5 13.5 4 4"
              stroke="#666"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <span>Search by title...</span>
        </div>

        <div className="rs-filters">
          <div className="rs-select">
            All Status <Chevron />
          </div>
          <div className="rs-select">
            Survey <Chevron />
          </div>
          <div className="rs-select">
            Sort by <Chevron />
          </div>
          <div className="rs-select rs-select--date">
            dd-mm-yyyy
            <svg viewBox="0 0 16 16" width="15" height="15" fill="none">
              <rect
                x="2"
                y="3"
                width="12"
                height="11"
                rx="1.5"
                stroke="#111"
                strokeWidth="1.6"
              />
              <path d="M2 6.5h12" stroke="#111" strokeWidth="2.2" />
            </svg>
          </div>
          <div className="rs-red">See Low Scorer</div>
          <div className="rs-red rs-red--bold">📊 Export Overall Quiz</div>
          <div className="rs-red rs-red--bold">📊 Export Overall Survey</div>
        </div>
      </div>

      <h2 className="rs-active">Active Surveys</h2>
      <h2 className="rs-closed">Closed Surveys</h2>

      <div className="rs-grid">
        {CARDS.map((card, i) => (
          <div className="rs-card" key={i}>
            <h3>{card.title}</h3>
            <p>
              {card.questions} questions (
              <span className="rs-expired">Expired duration</span>)
            </p>
            <div className="rs-btn">See Results</div>
            <div className="rs-btn">Performance Insight</div>
            <div className="rs-btn">Manage</div>
            <div className="rs-btn rs-btn--disabled">
              <svg viewBox="0 0 16 16" width="18" height="18" fill="none">
                <rect
                  x="1"
                  y="1"
                  width="14"
                  height="14"
                  rx="2"
                  fill="#4cc38a"
                />
                <path
                  d="m4 8.2 2.8 2.8L12 5.5"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Already Announced
            </div>
            <div className="rs-btn">Re-Open {card.title}</div>
            <div className="rs-btn">📋 Duplicate &amp; Edit</div>
            <div className="rs-btn rs-btn--red">Delete</div>
          </div>
        ))}
      </div>
    </div>

    {SHOW_FAKE_SCROLLBAR && (
      <div className="rs-scrollbar" aria-hidden="true">
        <div className="rs-scrollbar__thumb" />
      </div>
    )}
  </div>
);

export default RestrictedScreen;
